import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { FileText } from 'lucide-react';
import axios from 'axios';
import { uploadFile } from '@/lib/uploadFile';
import CommentItem from '@/components/comments/CommentItem';
import CommentInputBar from '@/components/comments/CommentInputBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CommentsTab({ classId, students = [] }) {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const headers = { headers: { Authorization: `Bearer ${token}` } };

  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [recipientId, setRecipientId] = useState('');
  const scrollRef = useRef(null);

  const isTeacherOrAdmin = user?.role === 'teacher' || user?.role === 'admin';

  const fetchComments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/comments/${classId}`, headers);
      setComments(res.data);
    } catch (err) {
      console.error('[CommentsTab] fetch error:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, token]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [comments]);

  const studentNameById = useCallback((sid) => {
    const s = students.find(st => (st.id || st.user_id) === sid);
    return s ? `${s.first_name} ${s.last_name}` : 'Estudiante';
  }, [students]);

  const recipientDisplayName = recipientId ? studentNameById(recipientId) : 'Todos los estudiantes';

  const handleSend = async () => {
    if (!text.trim() && !file) return;
    setSending(true);
    try {
      const uploadedFiles = [];
      if (file) uploadedFiles.push(await uploadFile(file, token));
      await axios.post(`${API}/comments`, {
        class_id: classId,
        text: text.trim(),
        files: uploadedFiles,
        recipient_id: recipientId || null,
      }, headers);
      setText('');
      setFile(null);
      fetchComments();
    } catch (err) {
      console.error('[CommentsTab] send error:', err);
      toast.error(t('error'));
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await axios.delete(`${API}/comments/${commentId}`, headers);
      fetchComments();
    } catch (err) {
      console.error('[CommentsTab] delete error:', err);
      toast.error(t('error'));
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-320px)] min-h-[400px]" data-testid="comments-tab">
      <ScrollArea className="flex-1 px-1" ref={scrollRef}>
        <div className="space-y-4 py-4">
          {comments.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>{t('no_items')}</p>
            </div>
          ) : (
            comments.map(c => (
              <CommentItem
                key={c.id}
                comment={c}
                currentUserId={user?.id}
                isTeacherOrAdmin={isTeacherOrAdmin}
                recipientName={c.recipient_id ? studentNameById(c.recipient_id) : null}
                onDelete={handleDelete}
                tTeacher={t('teacher')}
              />
            ))
          )}
        </div>
      </ScrollArea>

      <CommentInputBar
        text={text}
        setText={setText}
        file={file}
        setFile={setFile}
        sending={sending}
        onSend={handleSend}
        isTeacherOrAdmin={isTeacherOrAdmin}
        students={students}
        recipientId={recipientId}
        setRecipientId={setRecipientId}
        recipientDisplayName={recipientDisplayName}
        placeholder={recipientId ? `Mensaje privado para ${recipientDisplayName}...` : t('write_comment')}
      />
    </div>
  );
}
